import React from 'react';
import { MailWarning } from 'lucide-react';
import { Spinner } from '../ui';

/**
 * A supplier's contact state where the platform holds no address yet: Robbie
 * still looking it up ("Finding contact…", violet — Robbie is the one
 * working), or no email ("No email", warn). Renders nothing once an address
 * exists. `pill` is the discovery card, `chip` a thread row, `icon` a dense
 * table row.
 */
export type ContactEmailBadgeVariant = 'pill' | 'chip' | 'icon';

interface ContactEmailBadgeProps {
  hasContactEmail: boolean;
  findingContact?: boolean;
  variant: ContactEmailBadgeVariant;
  /** Tooltip for the "No email" state; each surface says what happens next. */
  noEmailTitle: string;
}

const SHAPE: Record<Exclude<ContactEmailBadgeVariant, 'icon'>, string> = {
  pill: 'gap-s1 rounded-pill border px-s2 py-[1px] text-meta',
  chip: 'shrink-0 gap-[3px] rounded-[4px] px-[5px] py-[1px] text-[10px]',
};

const FINDING_TITLE = 'Robbie is looking up this supplier’s contact on its website';

export const ContactEmailBadge: React.FC<ContactEmailBadgeProps> = ({ hasContactEmail, findingContact, variant, noEmailTitle }) => {
  if (hasContactEmail) return null;

  if (variant === 'icon') {
    return findingContact ? (
      <span role="img" aria-label="Finding contact" title={FINDING_TITLE} className="inline-flex shrink-0">
        <Spinner tone="robbie" size="sm" />
      </span>
    ) : (
      <MailWarning className="h-3 w-3 shrink-0 text-warn-ink" aria-label={noEmailTitle} />
    );
  }

  const icon = variant === 'pill' ? 'h-3 w-3' : 'h-[10px] w-[10px]';
  if (findingContact) {
    return (
      <span
        className={`inline-flex items-center font-semibold text-robbie-ink ${SHAPE[variant]} ${variant === 'pill' ? 'border-robbie-border bg-robbie-bg' : 'bg-robbie-bg'}`}
        title={FINDING_TITLE}
      >
        <Spinner tone="robbie" size="sm" /> Finding contact…
      </span>
    );
  }
  return (
    <span
      className={`inline-flex items-center font-semibold text-warn-ink ${SHAPE[variant]} ${variant === 'pill' ? 'border-warn-border bg-warn-bg' : 'bg-warn-bg'}`}
      title={noEmailTitle}
    >
      <MailWarning className={icon} /> No email
    </span>
  );
};

export default ContactEmailBadge;
